"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { GetVideoResponseType } from "@/features/storage/sheets/hooks/useGetVideo";
import { client } from "@/lib/hono";
import toast from "@/lib/toast";
import { rtlLanguages, SrclangType } from "@yedalabs/schema";
import { useQueryClient } from "@tanstack/react-query";
import { HelpCircle, LoaderCircle, Plus } from "lucide-react";
import { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";
import { TabValue } from "../../main";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import CaptionsEditor, {
    type CaptionsEditorHandle,
} from "./editor/CaptionsEditor";
import type { TranscriptionBlock } from "./editor/lib/utils";
import HelpModal from "./editor/HelpModal";

type Props = {
    captionsId: string;
    srt: string;
    srclang: SrclangType;
    published: boolean;
    transcription: TranscriptionBlock[] | null;
    setReloadVideoKey: (key: number) => void;
    captions: GetVideoResponseType["data"]["captions"];
    setCurrentCaptionsId: Dispatch<SetStateAction<string | undefined>>;
    changeTab: (value: TabValue) => void;
};

export default function EditCaptions({
    captionsId,
    srt,
    srclang,
    published: initialPublished,
    transcription,
    setReloadVideoKey,
    captions,
    setCurrentCaptionsId,
    changeTab,
}: Props) {
    const { dir } = useLocale();
    const [dirty, setDirty] = useState(false);
    const [loading, setLoading] = useState(false);
    const [published, setPublished] = useState(initialPublished);
    const [helpOpen, setHelpOpen] = useState(false);
    const editorRef = useRef<CaptionsEditorHandle>(null);
    const rtl = rtlLanguages.includes(srclang);

    const queryClient = useQueryClient();

    const handleSave = useCallback(async () => {
        const nextSrt = editorRef.current?.getSrt() ?? srt;
        if (!nextSrt.trim()) {
            toast.error(t("toast.error.default"), {
                keyName: "toast.error.default",
            });
            return;
        }

        setLoading(true);

        const nextTranscription = editorRef.current?.hasTranscriptionChanges()
            ? editorRef.current.getTranscription()
            : null;

        const res = await client.api.storage.captions[
            ":captionsId"
        ].update.$post({
            param: { captionsId },
            json: {
                srt: nextSrt,
                published,
                ...(nextTranscription
                    ? { extendedTranscription: nextTranscription }
                    : {}),
            },
        });

        if (!res.ok) {
            toast.error(t("toast.error.update-captions"), {
                keyName: "toast.error.update-captions",
            });
            setLoading(false);
            return;
        }

        queryClient.invalidateQueries({
            queryKey: ["get-captions", captionsId],
        });

        setReloadVideoKey(Date.now());
        toast.success(t("toast.success.changes-saved"), {
            keyName: "toast.success.changes-saved",
        });
        setLoading(false);
        editorRef.current?.markClean();
        setDirty(false);
    }, [captionsId, published, srt, queryClient, setReloadVideoKey]);

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!dirty || loading) return;
        handleSave();
    };

    return (
        <form onSubmit={onSubmit} className="mt-4 flex w-full flex-col gap-2">
            <HelpModal open={helpOpen} onOpenChange={setHelpOpen} />

            <div className="flex flex-row items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Label htmlFor="srt">
                        {t("video-sheet.captions-tab.title")}
                    </Label>
                    <Select
                        dir={dir}
                        value={captionsId}
                        onValueChange={(value) => {
                            if (value === "plus") {
                                changeTab("translations");
                            } else {
                                setCurrentCaptionsId(value);
                            }
                        }}
                    >
                        <SelectTrigger className="h-9 w-fit gap-1.5">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {captions.map((caption) => (
                                <SelectItem key={caption.id} value={caption.id}>
                                    {caption.label}
                                </SelectItem>
                            ))}
                            <SelectItem value="plus">
                                <div className="flex flex-row items-center gap-1">
                                    <span>
                                        {t(
                                            "video-sheet.captions-tab.add-language"
                                        )}
                                    </span>
                                    <Plus className="size-3.5" />
                                </div>
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex flex-row items-center gap-2">
                    <Checkbox
                        id="published"
                        checked={published}
                        onCheckedChange={(checked) => {
                            setPublished(!!checked);
                            setDirty(true);
                        }}
                    />
                    <Label
                        htmlFor="published"
                        onClick={() => {
                            setPublished((prev) => !prev);
                            setDirty(true);
                        }}
                    >
                        {t("video-sheet.captions-tab.published-checkbox")}
                    </Label>
                </div>
            </div>

            <CaptionsEditor
                key={captionsId}
                ref={editorRef}
                srt={srt}
                transcription={transcription}
                rtl={rtl}
                onDirtyChange={setDirty}
                onSaveHotkey={handleSave}
                setHelpOpen={setHelpOpen}
            />

            <div className="mt-2 flex w-full">
                <Button
                    disabled={loading || !dirty}
                    type="submit"
                    className="w-fit"
                >
                    {loading ? (
                        <LoaderCircle className="size-4 animate-spin" />
                    ) : (
                        t(
                            "video-sheet.captions-tab.save-changes",
                            undefined,
                            undefined
                        )
                    )}
                </Button>
            </div>
        </form>
    );
}
